import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ToolSet } from "ai";
import type { Chat } from "@ai-sdk/react";

import { McpToolSet } from "components/AssistantPanel/types";

import { ChatApprovalMode, ChatMode, ChatUIMessage } from "../types";

export type ChatBudgetUsage = {
  tokensResetAt: number;
  totalTokens: number;
  usedTokens: number;
  usedTokensCurrentSession: number;
};

type ChatState = {
  chat: Chat<ChatUIMessage> | undefined;
  pendingMessage: string | undefined;
  composerDraft: string | undefined;
  composerValue: string;
  conversationError: string | undefined;
  focusKey: number;
  conversationVersion: number;
  integrationId: string | undefined;
  model: string | undefined;
  budgetUsage: ChatBudgetUsage | undefined;
  mode: ChatMode;
  modeByChat: Record<string, ChatMode>;
  approvalMode: ChatApprovalMode;
  mcpFilteredTools: McpToolSet;
  frontendTools: ToolSet;
  isHistoryOpen: boolean;
};

type ChatActions = {
  startChat: (chat: Chat<ChatUIMessage>, pendingMessage: string) => void;
  appendChat: (message: string) => void;
  clearComposerDraft: () => void;
  setComposerValue: (value: string) => void;
  openChat: (params: {
    chat: Chat<ChatUIMessage>;
    version: number;
    integrationId: string | undefined;
    model: string | undefined;
  }) => void;
  resetChat: () => void;
  clearPendingMessage: () => void;
  setConversationError: (error: string | undefined) => void;
  setConversationVersion: (version: number) => void;
  setIntegrationId: (id: string | undefined) => void;
  setModel: (model: string | undefined) => void;
  setBudgetUsage: (usage: ChatBudgetUsage) => void;
  setMode: (mode: ChatMode) => void;
  setApprovalMode: (mode: ChatApprovalMode) => void;
  setMcpFilteredTools: (tools: McpToolSet) => void;
  setFrontendTools: (tools: ToolSet) => void;
  toggleHistory: () => void;
};

type ChatStore = ChatState & ChatActions;

const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      chat: undefined,
      pendingMessage: undefined,
      composerDraft: undefined,
      composerValue: "",
      conversationError: undefined,
      focusKey: 0,
      conversationVersion: 0,
      integrationId: undefined,
      model: undefined,
      budgetUsage: undefined,
      mode: ChatMode.ASK,
      modeByChat: {},
      mcpFilteredTools: {},
      frontendTools: {},
      approvalMode: ChatApprovalMode.MANUAL,
      isHistoryOpen: false,

      startChat: (chat, pendingMessage) => {
        get().chat?.stop();
        set({
          chat,
          pendingMessage,
          conversationVersion: 0,
          focusKey: get().focusKey + 1,
          modeByChat: { ...get().modeByChat, [chat.id]: get().mode },
        });
      },

      appendChat: (message) => {
        set({
          composerDraft: message,
          focusKey: get().focusKey + 1,
        });
      },

      clearComposerDraft: () => {
        set({ composerDraft: undefined });
      },

      setComposerValue: (value) => {
        set({ composerValue: value });
      },

      openChat: ({ chat, version, integrationId, model }) => {
        get().chat?.stop();
        set({
          chat,
          conversationVersion: version,
          integrationId,
          model,
          mode: get().modeByChat[chat.id] ?? ChatMode.ASK,
          focusKey: get().focusKey + 1,
        });
      },

      resetChat: () => {
        get().chat?.stop();
        set({
          chat: undefined,
          pendingMessage: undefined,
          composerDraft: undefined,
          conversationError: undefined,
          mode: ChatMode.ASK,
        });
      },

      clearPendingMessage: () => {
        set({ pendingMessage: undefined });
      },

      setConversationError: (error) => {
        set({ conversationError: error });
      },

      setConversationVersion: (version) => {
        set({ conversationVersion: version });
      },

      setIntegrationId: (id) => {
        set({ integrationId: id, budgetUsage: undefined });
      },

      setModel: (model) => {
        set({ model });
      },

      setBudgetUsage: (usage) => {
        set({ budgetUsage: usage });
      },

      setMode: (mode) => {
        const chat = get().chat;
        set({
          mode,
          ...(chat && { modeByChat: { ...get().modeByChat, [chat.id]: mode } }),
        });
      },

      setApprovalMode: (approvalMode) => {
        set({ approvalMode });
      },

      setMcpFilteredTools: (tools) => {
        set({ mcpFilteredTools: tools });
      },

      setFrontendTools: (tools) => {
        set({ frontendTools: tools });
      },

      toggleHistory: () => {
        set({ isHistoryOpen: !get().isHistoryOpen });
      },
    }),
    {
      name: "chat-store",
      partialize: (state) => ({
        integrationId: state.integrationId,
        model: state.model,
        modeByChat: state.modeByChat,
      }),
      version: 1,
    }
  )
);

export default useChatStore;
